import api from '@pray/shared/utils/api';

/**
 * Datasets stored in the nightly rollup. One marginal table per dimension: a full cross-tab would
 * bloat the payload and buy nothing, since a leak that only exists in a two-way interaction is not
 * something a ranked list can express anyway.
 */
export type LeadPipelineDataset =
  | 'pipe_signin'
  | 'pipe_artist'
  | 'pipe_country'
  // Capture by the surface that showed the offer. A person can see more than one surface and is
  // counted once in each, so these rows are deliberately not a partition of `pipe_signin` and must
  // never be summed to reconstruct it.
  | 'pipe_placement'
  | 'deliv_signin'
  | 'deliv_placement'
  | 'method'
  | 'sf_spend'
  | 'sf_leads'
  | 'unparented';

/** One rollup row. Measures are sparse by dataset — only the ones that dataset writes are set. */
export type LeadPipelineMetric = {
  metric_date: string;
  dataset: LeadPipelineDataset;
  dim_key: string;
  dim_key2: string;
  impressed: number | null;
  led: number | null;
  valid: number | null;
  delivered: number | null;
  registrations: number | null;
  relay: number | null;
  impressions: number | null;
  retail_cents: number | null;
  wholesale_cents: number | null;
};

export type LeadPipelineArtist = {
  artist_id: number;
  artist_name: string;
  /** Also ran non-SuperFunnel campaigns in the window, so their spend cannot be attributed to lead
   *  delivery alone. The screening ladder drops them before any cost per lead is quoted. */
  is_multi_product: boolean;
};

export type LeadPipelineCampaign = {
  campaign: string;
  placement: string;
  leads: number;
  valid: number;
  sent: number;
};

/** Today so far. Partial by construction — excluded from every rate, delta and leak score. */
export type LeadPipelineTodayRow = {
  metric_date: string;
  placement: string;
  source: string;
  leads: number;
  valid: number;
  sent: number;
};

export type LeadPipelineResponse = {
  /** Spans twice the visible range: every reading is compared against the equally sized window
   *  before it, so a 30-day view needs 60 days on hand. */
  window: { from: string; to: string };
  /** Most recent day present in the rollup, so the UI states its own freshness rather than
   *  assuming last night's job ran. */
  rollup_through: string | null;
  metrics: LeadPipelineMetric[];
  artists: LeadPipelineArtist[];
  campaigns: LeadPipelineCampaign[];
  today_partial: LeadPipelineTodayRow[];
};

export type GetSystemCheckLeadPipelineParams = {
  rangeDays?: number;
  campaignLimit?: number;
};

export async function getSystemCheckLeadPipeline({
  rangeDays = 30,
  campaignLimit = 12,
}: GetSystemCheckLeadPipelineParams = {}): Promise<LeadPipelineResponse> {
  const url = '/web/studio/system-check/lead-pipeline';

  // Three levels of `data`, and all three are real:
  //   axios response .data -> the HTTP body
  //   sendObjectResponse wraps every payload as { responded_at, object, data: <payload> }
  //   the serializer's payload is itself { data: <this dashboard> }, matching the other checks
  // Unwrapped here rather than in the hook so panels get a flat object.
  const response = await api.executeGetRequest<{ data: { data: LeadPipelineResponse } }>(url, {
    range_days: rangeDays,
    campaign_limit: campaignLimit,
  });

  const payload = response.data?.data?.data;

  // Fail loudly on a shape change. Returning a half-unwrapped object instead lets `undefined`
  // travel into the derivation code and surface as "cannot read 'forEach'" three frames away.
  if (!payload || !Array.isArray(payload.metrics)) {
    throw new Error('Lead pipeline response did not contain a metrics array');
  }

  return payload;
}
